import RectButton from '@/components/Common/DesignSystem/RectButton/RectButton';
import { GlobalStoreCtx } from '@/stores/GlobalStore';
import { NolPromotionStoreCtx } from '@/stores/NolPromotionStore';
import { throttle } from '@/utils';
import { useScrollStore } from '@/utils/hooks';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import React, { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';

import styles from './index.module.scss';

const Tabs: FC = () => {
  const store = useContext(NolPromotionStoreCtx);
  const globalStore = useContext(GlobalStoreCtx);
  const scrollStore = useScrollStore();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isTabFix, setTabFix] = useState(false);
  const showSmartBanner = store.showSmartBanner;
  const buttons = useMemo(() => {
    return [
      <>
        NOL
        <br />
        {'50% 쿠폰'}
      </>,
      <>
        호텔
        <br />
        즉시 할인
      </>,
      <>
        NOL
        <br /> {'10% 적립'}
      </>,
      <>
        공항 라운지
        <br /> {'연 2회'}
      </>,
    ];
  }, []);

  const [tab0offset, tab1offset, tab2offset, tab3offset] = useMemo(() => {
    const smartBannerHeight = showSmartBanner ? 86 : 0;
    const navHeight = 48;
    const tabHeight = 64;
    const offset = 10;

    // ref 영역에 display:contents 를 적용하면 offsetTop 이 0으로 나온다. 따라서 display:contents를 적용하지 않은 영역까지 내려가서 offsetTop을 구한다.
    // v4 현재 ref -> impression -> gridItem 구조
    const _tab0offset =
      ((store.anchorTab0Ref?.current?.firstChild?.firstChild as HTMLDivElement)?.offsetTop || 0) -
      navHeight -
      smartBannerHeight -
      tabHeight -
      offset;
    const _tab1offset =
      ((store.anchorTab1Ref?.current?.firstChild?.firstChild as HTMLDivElement)?.offsetTop || 0) -
      navHeight -
      smartBannerHeight -
      tabHeight -
      offset;
    const _tab2offset =
      ((store.anchorTab2Ref?.current as HTMLDivElement)?.offsetTop || 0) -
      navHeight -
      smartBannerHeight -
      tabHeight -
      offset;
    const _tab3offset =
      ((store.anchorTab3Ref?.current as HTMLDivElement)?.offsetTop || 0) -
      navHeight -
      smartBannerHeight -
      tabHeight -
      offset;
    return [_tab0offset, _tab1offset, _tab2offset, _tab3offset];
  }, [showSmartBanner, store.anchorTab0Ref, store.anchorTab1Ref, store.anchorTab2Ref]);

  useEffect(() => {
    const checkTabPosition = (): void => {
      const smartBannerHeight = showSmartBanner ? 86 : 0;
      const navHeight = 48;
      const tabYPosition = containerRef.current?.getClientRects()[0].y || 0;
      const shouldFix = tabYPosition - navHeight - smartBannerHeight <= 0;
      setTabFix(shouldFix);
    };

    // 60 fps, 1 fps 16.6ms
    const throttledPosition = throttle(checkTabPosition, 8);
    window.addEventListener('scroll', throttledPosition);

    return () => {
      window.removeEventListener('scroll', throttledPosition);
    };
  }, [globalStore.ShowSmartBanner, showSmartBanner]);

  useEffect(() => {
    // update top offset
    store.showSmartBanner = globalStore.ShowSmartBanner && scrollStore.IsGoingUp;
  }, [globalStore.ShowSmartBanner, scrollStore.IsGoingUp]);

  useEffect(() => {
    const onScroll = throttle((): void => {
      const yPosition = scrollStore.position.y;
      if (yPosition < tab0offset) {
        store.activeIndex = -1;
      } else if (yPosition < tab1offset) {
        store.activeIndex = 0;
      } else if (yPosition < tab2offset) {
        store.activeIndex = 1;
      } else if (yPosition < tab3offset) {
        store.activeIndex = 2;
      } else {
        store.activeIndex = 3;
      }
    }, 16);

    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [tab0offset, tab1offset, tab2offset, tab3offset]);

  return (
    <div className={styles.container} ref={containerRef}>
      <div
        className={clsx(
          styles.fixContainer,
          isTabFix && styles.fixed,
          isTabFix && showSmartBanner && styles.fixedMarginTop,
        )}
      >
        <div className={styles.buttonsContainer}>
          {buttons.map((title, index) => (
            <RectButton.Blue
              className={clsx(styles.item, store.activeIndex !== index ? styles.inactive : '')}
              as={'button'}
              key={index}
              onClick={() => store.onVersion3TabClick(index)}
            >
              {title}
            </RectButton.Blue>
          ))}
        </div>
      </div>
    </div>
  );
};

export default observer(Tabs);
